/**
 * ParticleShapes — geometry tessellation for the platformer particle
 * stack (split out of ParticleSystem.js along the rendering seam).
 *
 * Each pooled Graphics node is tessellated ONCE, in white, at the BASE
 * size. Colour comes from `node.tint`, size from `node.scale` at
 * runtime, so `buildShape` only ever runs on spawn (or when a pooled
 * node changes shape) — never per frame.
 */

import { BASE } from './ParticleConfig.js';

/** Tessellate `shape` into the Graphics node `g`. */
export function buildShape(g, shape) {
    g.clear();
    if (shape === 'square') {
        g.rect(-BASE, -BASE, BASE * 2, BASE * 2);
        g.fill(0xffffff);
    } else if (shape === 'star') {
        starPath(g, BASE, BASE * 0.45);
        g.fill(0xffffff);
    } else if (shape === 'spark') {
        // Elongated streak (rotated to the velocity vector each frame).
        const len = BASE * 2.6;
        const wid = BASE * 0.5;
        g.rect(-len / 2, -wid / 2, len, wid);
        g.fill(0xffffff);
    } else if (shape === 'confetti') {
        // Flat rectangular "paper" — wider than tall so the per-frame
        // tumble (rotation) reads as fluttering confetti. Drawn white;
        // recoloured per-particle from the palette via node.tint.
        const w = BASE * 1.5, h = BASE * 0.8;
        g.rect(-w / 2, -h / 2, w, h);
        g.fill(0xffffff);
    } else if (shape === 'soft') {
        // Soft / blurred blob: stack translucent rings (largest →
        // smallest) so the overlapping fills accumulate toward the
        // centre, faking a radial falloff with NO texture. node.tint
        // recolours it; blend 'add' turns it into a glow (embers,
        // magic sparkles, fog, fireflies).
        const rings = 6;
        for (let i = rings; i >= 1; i--) {
            g.circle(0, 0, (BASE * i) / rings);
            g.fill({ color: 0xffffff, alpha: 0.18 });
        }
    } else {
        g.circle(0, 0, BASE);
        g.fill(0xffffff);
    }
}

function starPath(g, outer, inner) {
    const pts = 5;
    for (let i = 0; i < pts * 2; i++) {
        const r = i % 2 === 0 ? outer : inner;
        const ang = (Math.PI / pts) * i - Math.PI / 2;
        const x = Math.cos(ang) * r;
        const y = Math.sin(ang) * r;
        if (i === 0) g.moveTo(x, y);
        else g.lineTo(x, y);
    }
    g.closePath();
}
